import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CardMedia,
  Input,
  TextField,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState, ChangeEvent } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import {
  useAddSubAdminMutation,
  useLazyGetSubAdminByIdQuery,
  useUpdateSubAdminMutation,
} from "../../service/subAdmin";
import { UploadMedia } from "../../utils/uploadMedia";
import { showError, showToast } from "../../constants/toasts";
import { isValidInput } from "../../utils/validations";
import { useGetDropDownListQuery } from "../../service/accessRole";

const AddSubadmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");

  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [countryCode, setCountryCode] = useState("+91");

  // API hooks
  const [getById] = useLazyGetSubAdminByIdQuery();
  const [addSubAdmin] = useAddSubAdminMutation();
  const [updateSubAdmin] = useUpdateSubAdminMutation();
  const { data: roleDropDownData } = useGetDropDownListQuery();
  const subadminrole =
    roleDropDownData?.data?.map((item: { name: string; _id: string }) => ({
      role: item?.name,
      _id: item?._id,
    })) ?? [];

  console.log(subadminrole, roleDropDownData, "subadminrole,roleDropDownData");

  const isAdd = location.pathname === "/manage-subadmin/add";

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required("First Name is required")
      .matches(/^[a-zA-Z\s]*$/, "First name can only contain letters")
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must be at most 50 characters"),
    lastName: Yup.string()
      .required("Last Name is required")
      .matches(/^[a-zA-Z\s]*$/, "Last name can only contain letters")
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must be at most 50 characters"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format")
      .max(100, "Email must be at most 100 characters"),
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9]+$/, "Phone number must contain only numbers")
      .min(6, "Phone number must be at least 6 digits")
      .max(15, "Phone number must be at most 15 digits"),
    roleId: Yup.string().required("Role is required"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      roleId: "",
      profileImage: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const body = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phoneNumber,
          countryCode: "+" + countryCode.replace("+", ""),
          image: values.profileImage || undefined,
          accessRole: values.roleId,
        };

        let response;
        if (isAdd) {
          response = await addSubAdmin(body).unwrap();
          if (response.code === 200) {
            showToast("Sub-admin added successfully");
            navigate("/manage-subadmin");
          }
        } else if (id) {
          response = await updateSubAdmin({ id, body }).unwrap();
          if (response.code === 200) {
            showToast("Sub-admin updated successfully");
            navigate("/manage-subadmin");
          }
        }
      } catch (error: any) {
        console.error("Error saving sub-admin:", error);
        showError(error?.data?.message || "Failed to save sub-admin");
      }
    },
  });

  const handlePhoneChange = (phone: string, country: any) => {
    const phoneWithoutCode = phone.replace(country.dialCode, "");
    formik.setFieldValue("phoneNumber", phoneWithoutCode);
    setCountryCode("+" + country.dialCode);
    setPhoneDisplay(phone);
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !files[0]) return;

    const file = files[0];

    if (!file.type.includes("image")) {
      setAlertType(0);
      setShowAlert(true);
      setAlertMessage("This field only accepts images.");
      return;
    }

    try {
      const res = await UploadMedia(file);
      if (res?.code === 200) {
        const imageUrl = res.data;
        formik.setFieldValue("profileImage", imageUrl);
        setImage(imageUrl);
        setFileName(file.name);
        showToast("Image uploaded successfully.");
      } else {
        showError(res?.message || "Failed to upload image");
      }
    } catch (e) {
      console.error("Error uploading image:", e);
      showError("Error uploading image. Please try again.");
    }
  };

  React.useEffect(() => {
    const fetch = async () => {
      if (!isAdd && id) {
        try {
          const res = await getById({ id }).unwrap();
          if (res.code === 200) {
            const d = res.data;
            formik.setValues({
              firstName: d?.firstName || "",
              lastName: d?.lastName || "",
              email: d?.email || "",
              phoneNumber: d?.phone || "",
              roleId: d?.accessRole?._id || "",
              profileImage: d?.image || "",
            });
            if (d?.image) setImage(d?.image);
            if (d?.countryCode && d?.phone) {
              setPhoneDisplay(`${d?.countryCode}${d?.phone}`);
              setCountryCode(d?.countryCode.replaceAll("+", ""));
            }
          }
        } catch (error: any) {
          console.error("Error fetching sub-admin:", error);
          showError(error?.data?.message || "Failed to fetch sub-admin");
          navigate("/manage-subadmin");
        }
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAdd]);

  return (
    <>
      <div className="main_title">
        <h1>Add Sub-Admin</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-subadmin")}>
            Manage Sub-Admin
          </button>{" "}
          - Add Sub-Admin
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="icon-button-file">Upload Image</label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="photo" />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          setFileName("");
                          formik.setFieldValue("profileImage", "");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg",
                      }}
                      onChange={handleImageUpload}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/user_placeholder.png"
                          alt="icon"
                        />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
              </div>

              <div className="control_group w_33">
                <label htmlFor="firstName">First Name</label>
                <TextField
                  id="firstName"
                  name="firstName"
                  placeholder="Enter First Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.firstName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length < 40) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="lastName">Last Name</label>
                <TextField
                  id="lastName"
                  name="lastName"
                  placeholder="Enter Last Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.lastName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length < 40) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </div>

              <div className="control_group w_33">
                <label htmlFor="email">Email Id</label>
                <TextField
                  id="email"
                  name="email"
                  placeholder="Enter Email Address"
                  hiddenLabel
                  fullWidth
                  value={formik.values.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length < 100) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </div>

              <div className="control_group w_33">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  inputProps={{ id: "phone" }}
                  country={"in"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ width: "100%" }}
                  value={phoneDisplay}
                  onChange={handlePhoneChange}
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {formik.errors.phoneNumber}
                  </Typography>
                )}
              </div>

              <div className="control_group w_33">
                <label htmlFor="role">Role</label>
                <Autocomplete
                  disablePortal
                  options={subadminrole}
                  getOptionLabel={(o) => o.role}
                  value={
                    subadminrole?.find(
                      (r: { _id: string }) => r._id === formik.values.roleId
                    ) || null
                  }
                  onChange={(_, val: any) => {
                    formik.setFieldValue("roleId", val?._id || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Role"
                      hiddenLabel
                      error={
                        formik.touched.roleId && Boolean(formik.errors.roleId)
                      }
                      helperText={formik.touched.roleId && formik.errors.roleId}
                    />
                  )}
                />
              </div>
            </div>

            <div className="form_btn">
              <Button className="btn btn_primary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddSubadmin;
